import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router';
import {AppDispatch} from '@store';
import {selectCalendarSnapshotIndex, fetchCalendarRange} from '@store/slices/calendar';
import {selectCoveragePlans} from '@store/slices/coveragePlans';
import {getSystemSnapshot} from '@store/slices/systems';
import {
  toISODateString,
  getMonthStart,
  getMonthEnd,
  findFirstIdAfterOrEqual,
  findPlanIdExactOrAfter,
  addMonths,
} from '@utils/common';
import {ERoutes} from '@constants/routes';

export const useSystemHistory = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {id} = useParams<{id: string}>();

  const snapshotIndex = useSelector(selectCalendarSnapshotIndex);
  const plansForSystem = useSelector(selectCoveragePlans);

  const plannedDates = useMemo(() => plansForSystem.map(p => p.date), [plansForSystem]);

  const handleCompare = useCallback(
    async (
      left: Date,
      right: Date,
      mode: 'data-data' | 'plan-plan' | 'data-plan' | 'plan-data' = 'data-data',
    ) => {
      const before_date = toISODateString(getMonthStart(left));
      const after_date = toISODateString(getMonthEnd(right));
      const payload = await dispatch(
        fetchCalendarRange({before_date, after_date}),
      ).unwrap();

      const leftWasEarlier = left <= right;
      const leftISO = toISODateString(leftWasEarlier ? left : right);
      const rightISO = toISODateString(leftWasEarlier ? right : left);

      const dataSnapshotsAsc = payload.snapshotIndex
        .filter(s => !plannedDates.includes(s.dateISO))
        .sort((a, b) => a.dateISO.localeCompare(b.dateISO));
      const plannedSnapshotsAsc = payload.snapshotIndex
        .filter(s => plannedDates.includes(s.dateISO))
        .sort((a, b) => a.dateISO.localeCompare(b.dateISO));

      const pickDataAfterOrEqual = (targetISO: string): number | null =>
        findFirstIdAfterOrEqual(dataSnapshotsAsc, targetISO);

      const pickPlanExactOrAfter = (
        targetISO: string,
        strictExact: boolean,
      ): number | null =>
        findPlanIdExactOrAfter(plannedSnapshotsAsc, targetISO, strictExact);

      let before_snapshot_id: number | null = null;
      let after_snapshot_id: number | null = null;
      switch (mode) {
        case 'data-data':
          before_snapshot_id = pickDataAfterOrEqual(leftISO);
          after_snapshot_id = pickDataAfterOrEqual(rightISO);
          break;
        case 'plan-plan':
          before_snapshot_id = pickPlanExactOrAfter(leftISO, true);
          after_snapshot_id = pickPlanExactOrAfter(rightISO, true);
          break;
        case 'data-plan': {
          before_snapshot_id = pickDataAfterOrEqual(leftISO);
          after_snapshot_id = pickPlanExactOrAfter(rightISO, false);
          break;
        }
        case 'plan-data': {
          before_snapshot_id = pickPlanExactOrAfter(leftISO, false);
          after_snapshot_id = pickDataAfterOrEqual(rightISO);
          break;
        }
      }

      const needFallback =
        mode === 'data-data'
          ? before_snapshot_id == null || after_snapshot_id == null
          : mode === 'data-plan'
            ? before_snapshot_id == null
            : mode === 'plan-data'
              ? after_snapshot_id == null
              : before_snapshot_id == null || after_snapshot_id == null;

      if (needFallback) {
        const earliest = leftWasEarlier ? left : right;
        const latest = leftWasEarlier ? right : left;
        const fbBefore = toISODateString(getMonthStart(addMonths(earliest, -12)));
        const fbAfter = toISODateString(getMonthEnd(latest));
        const payloadFB = await dispatch(
          fetchCalendarRange({before_date: fbBefore, after_date: fbAfter}),
        ).unwrap();

        const dataSnapshotsAscFB = payloadFB.snapshotIndex
          .filter(s => !plannedDates.includes(s.dateISO))
          .sort((a, b) => a.dateISO.localeCompare(b.dateISO));
        const plannedSnapshotsAscFB = payloadFB.snapshotIndex
          .filter(s => plannedDates.includes(s.dateISO))
          .sort((a, b) => a.dateISO.localeCompare(b.dateISO));

        const pickDataAfterOrEqualFB = (targetISO: string): number | null => {
          if (!dataSnapshotsAscFB || dataSnapshotsAscFB.length === 0) return null;
          const found = dataSnapshotsAscFB.find(s => s.dateISO >= targetISO);
          return found ? found.id : dataSnapshotsAscFB[dataSnapshotsAscFB.length - 1].id;
        };
        const pickPlanExactOrAfterFB = (
          targetISO: string,
          strictExact: boolean,
        ): number | null =>
          findPlanIdExactOrAfter(plannedSnapshotsAscFB, targetISO, strictExact);

        switch (mode) {
          case 'data-data':
            if (before_snapshot_id == null)
              before_snapshot_id = pickDataAfterOrEqualFB(leftISO);
            if (after_snapshot_id == null)
              after_snapshot_id = pickDataAfterOrEqualFB(rightISO);
            break;
          case 'plan-plan':
            if (before_snapshot_id == null)
              before_snapshot_id = pickPlanExactOrAfterFB(leftISO, true);
            if (after_snapshot_id == null)
              after_snapshot_id = pickPlanExactOrAfterFB(rightISO, true);
            break;
          case 'data-plan':
            if (before_snapshot_id == null)
              before_snapshot_id = pickDataAfterOrEqualFB(leftISO);
            if (after_snapshot_id == null)
              after_snapshot_id = pickPlanExactOrAfterFB(rightISO, false);
            break;
          case 'plan-data':
            if (before_snapshot_id == null)
              before_snapshot_id = pickPlanExactOrAfterFB(leftISO, false);
            if (after_snapshot_id == null)
              after_snapshot_id = pickDataAfterOrEqualFB(rightISO);
            break;
        }
      }

      navigate(
        `/systems/${id}/compare?left=${leftISO}&right=${rightISO}&before_snapshot_id=${before_snapshot_id ?? ''}&after_snapshot_id=${after_snapshot_id ?? ''}&mode=${mode}`,
      );
    },
    [dispatch, navigate, id, plannedDates],
  );

  const handleShowSingle = useCallback(
    async (date: Date) => {
      const systemId = Number(id);
      const dateISO = toISODateString(date);

      const isPlannedTarget = plannedDates.includes(dateISO);
      let snapshot_id: number | null | undefined;
      if (!isPlannedTarget) {
        snapshot_id = snapshotIndex.find(s => s.dateISO === dateISO)?.id;
      }
      if (!snapshot_id) {
        const filtered = snapshotIndex
          .filter(s => s.dateISO <= dateISO && !plannedDates.includes(s.dateISO))
          .sort((a, b) => b.dateISO.localeCompare(a.dateISO));
        snapshot_id = filtered[0]?.id;
      }
      if (!snapshot_id) {
        const before_date = toISODateString(getMonthStart(addMonths(date, -12)));
        const after_date = toISODateString(getMonthEnd(date));
        const payload = await dispatch(
          fetchCalendarRange({before_date, after_date}),
        ).unwrap();
        const historical = payload.snapshotIndex
          .filter(s => s.dateISO <= dateISO && !plannedDates.includes(s.dateISO))
          .sort((a, b) => b.dateISO.localeCompare(a.dateISO));
        snapshot_id = historical[0]?.id ?? null;
      }

      if (!snapshot_id) return;
      await dispatch(getSystemSnapshot({system_id: systemId, snapshot_id})).unwrap();
    },
    [dispatch, id, snapshotIndex, plannedDates],
  );

  const handleShowPlan = useCallback(
    (date: Date) => {
      const dateISO = toISODateString(date);
      navigate(ERoutes.SYSTEM_PLAN_EDIT.replace(':id', String(id)) + `?date=${dateISO}`);
    },
    [navigate, id],
  );

  return {
    snapshotIndex,
    plannedDates,
    handleCompare,
    handleShowSingle,
    handleShowPlan,
  };
};
